import { useEditor, EditorContent, useEditorState, type Editor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import { useEffect, useRef, type ReactNode } from 'react';
import type { EditorView } from '@tiptap/pm/view';
import { cleanWordHtml } from './wordPaste';
import { BodyImage } from './BodyImageExtension';
import { useBodyImageUploads } from './useBodyImageUploads';

interface Props {
  value: string;
  onChange: (html: string) => void;
  uploadImage?: (file: File) => Promise<string>;
  onImageUploadError?: (err: unknown) => void;
}

interface ToolButtonProps {
  onClick: () => void;
  active?: boolean;
  label: string;
  children: ReactNode;
}

function ToolButton({ onClick, active = false, label, children }: ToolButtonProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={label}
      aria-label={label}
      aria-pressed={active}
      className={
        'flex h-[34px] min-w-[34px] items-center justify-center rounded-[7px] px-2 ' +
        'font-trirong_reg text-[15px] text-gray-600 transition-colors ' +
        'hover:bg-[#e7eed2] hover:text-lime-700 focus:outline-none focus-visible:ring-2 ' +
        'focus-visible:ring-lime-600/40 ' +
        (active ? 'bg-[#dde8c4] text-lime-800' : '')
      }
    >
      {children}
    </button>
  );
}

function Divider() {
  return <span className="mx-1.5 h-[22px] w-px self-center bg-hairline" aria-hidden="true" />;
}

const iconProps = {
  width: 17,
  height: 17,
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.7,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
  'aria-hidden': true,
};

export default function RichTextEditor({
  value,
  onChange,
  uploadImage,
  onImageUploadError,
}: Props) {
  // The editor's paste handler and post-update scan are defined by the upload
  // hook, which needs the editor instance — created below. Route through refs so
  // editorProps (fixed at creation) always calls the latest handler.
  const fileInputRef = useRef<HTMLInputElement>(null);
  const handlePasteRef = useRef<(view: EditorView, event: ClipboardEvent) => boolean>(() => false);
  const scanRef = useRef<() => void>(() => {});

  const editor = useEditor({
    extensions: [
      StarterKit,
      Link.configure({ openOnClick: false }),
      Placeholder.configure({ placeholder: 'Begin writing…' }),
      BodyImage,
    ],
    content: value,
    onUpdate: ({ editor }: { editor: Editor }) => {
      onChange(editor.getHTML());
      // Defer: don't re-dispatch a transaction from inside an update. The scan
      // uploads any googleusercontent/data: images a paste just dropped in.
      const scan = scanRef.current;
      queueMicrotask(() => scan());
    },
    editorProps: {
      attributes: {
        class:
          'prose prose-sm max-w-none min-h-[300px] px-6 py-5 text-[16px] leading-[1.75] ' +
          'font-trirong_light text-[#353f2a] focus:outline-none ' +
          'prose-headings:font-trirong_reg prose-headings:text-gray-800 ' +
          'prose-p:font-trirong_light prose-strong:text-gray-900 prose-a:text-lime-700 ' +
          'prose-a:font-trirong_reg prose-blockquote:border-l-[#9db39f] ' +
          'prose-blockquote:font-trirong_light prose-blockquote:not-italic prose-img:rounded-xl',
      },
      // Clean Word/Pages clipboard HTML before ProseMirror parses it: real
      // headings, no spacer gaps, no Word junk. See wordPaste.ts.
      transformPastedHTML: (html: string) => cleanWordHtml(html),
      handlePaste: (view, event) => handlePasteRef.current(view, event as ClipboardEvent),
    },
  });

  const { insertBlob, handlePaste, scanRemoteImages } = useBodyImageUploads({
    editor,
    uploadImage,
    onError: onImageUploadError,
  });
  handlePasteRef.current = handlePaste;
  scanRef.current = scanRemoteImages;

  // TipTap reads `content` only at creation, so a later external change to `value`
  // (e.g. a discard reset clearing the body) won't reach the document on its own.
  // Push it in when it diverges; emitUpdate:false avoids a feedback onChange, and the
  // getHTML guard skips no-op writes during normal typing so the cursor never jumps.
  useEffect(() => {
    if (editor && value !== editor.getHTML()) {
      editor.commands.setContent(value, { emitUpdate: false });
    }
    // setContent uses emitUpdate:false (no onUpdate), and initial content never
    // fires onUpdate — so scan here too, to upload any googleusercontent/data:
    // images present on mount or from an external value change.
    if (editor) {
      const scan = scanRef.current;
      queueMicrotask(() => scan());
    }
  }, [editor, value]);

  const active = useEditorState({
    editor,
    selector: ({ editor }: { editor: Editor | null }) => ({
      bold: editor?.isActive('bold') ?? false,
      italic: editor?.isActive('italic') ?? false,
      h2: editor?.isActive('heading', { level: 2 }) ?? false,
      h3: editor?.isActive('heading', { level: 3 }) ?? false,
      blockquote: editor?.isActive('blockquote') ?? false,
      bulletList: editor?.isActive('bulletList') ?? false,
      orderedList: editor?.isActive('orderedList') ?? false,
      link: editor?.isActive('link') ?? false,
    }),
  });

  if (!editor || !active) return null;

  const toggleLink = () => {
    if (editor.isActive('link')) {
      editor.chain().focus().unsetLink().run();
      return;
    }
    const url = window.prompt('Link URL');
    if (url) editor.chain().focus().setLink({ href: url }).run();
  };

  return (
    <div className="overflow-hidden rounded-[10px] border border-field bg-white transition-colors focus-within:border-lime-700 focus-within:ring-2 focus-within:ring-lime-600/20">
      <div className="flex flex-wrap items-center gap-0.5 border-b border-hairline bg-[#f4f7e7] px-2.5 py-2">
        <ToolButton label="Bold" active={active.bold} onClick={() => editor.chain().focus().toggleBold().run()}>
          <b>B</b>
        </ToolButton>
        <ToolButton label="Italic" active={active.italic} onClick={() => editor.chain().focus().toggleItalic().run()}>
          <span className="italic">I</span>
        </ToolButton>

        <Divider />

        <ToolButton label="Heading 2" active={active.h2} onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}>
          <span className="text-sm leading-none">H2</span>
        </ToolButton>
        <ToolButton label="Heading 3" active={active.h3} onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}>
          <span className="text-sm leading-none">H3</span>
        </ToolButton>
        <ToolButton label="Quote" active={active.blockquote} onClick={() => editor.chain().focus().toggleBlockquote().run()}>
          <span className="text-lg leading-none">&#8220;</span>
        </ToolButton>

        <Divider />

        <ToolButton label="Bullet list" active={active.bulletList} onClick={() => editor.chain().focus().toggleBulletList().run()}>
          <svg {...iconProps}><path d="M9 6h11M9 12h11M9 18h11" /><circle cx="4" cy="6" r="1.2" fill="currentColor" stroke="none" /><circle cx="4" cy="12" r="1.2" fill="currentColor" stroke="none" /><circle cx="4" cy="18" r="1.2" fill="currentColor" stroke="none" /></svg>
        </ToolButton>
        <ToolButton label="Numbered list" active={active.orderedList} onClick={() => editor.chain().focus().toggleOrderedList().run()}>
          <svg {...iconProps}><path d="M10 6h10M10 12h10M10 18h10M4 4v3M4 6h1M3 11h2l-2 2.5h2M3 17h2v3H3" /></svg>
        </ToolButton>

        <Divider />

        <ToolButton label={active.link ? 'Remove link' : 'Add link'} active={active.link} onClick={toggleLink}>
          <svg {...iconProps}><path d="M10 13a5 5 0 007 0l2-2a5 5 0 00-7-7l-1 1M14 11a5 5 0 00-7 0l-2 2a5 5 0 007 7l1-1" /></svg>
        </ToolButton>

        {uploadImage && (
          <>
            <Divider />
            <ToolButton label="Insert image" onClick={() => fileInputRef.current?.click()}>
              <svg {...iconProps}><rect x="3" y="4" width="18" height="16" rx="2" /><circle cx="8.5" cy="9.5" r="1.5" /><path d="M21 16l-5-5L5 20" /></svg>
            </ToolButton>
          </>
        )}
      </div>

      <input
        ref={fileInputRef}
        data-testid="insert-image-input"
        type="file"
        accept="image/png,image/jpeg,image/gif,image/webp"
        className="sr-only"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) void insertBlob(file);
          e.target.value = '';
        }}
      />
      <EditorContent editor={editor} />
    </div>
  );
}
